import { cva } from 'class-variance-authority';
import { X } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import {
  UNSTABLE_ToastRegion as AriaToastRegion,
  UNSTABLE_Toast as AriaToast,
  UNSTABLE_ToastContent as AriaToastContent,
  Text,
  Button,
  type QueuedToast,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';

import { toastQueue, type ToastContent } from './toast-queue';

const toastRegionVariants = cva([
  'fixed right-4 bottom-4 z-50 flex flex-col-reverse gap-2 outline-none',
  'data-[focus-visible]:outline-2 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-primary',
]);

const toastVariants = cva(
  [
    'max-w-[420px] rounded-md border border-foreground/20 bg-white p-4 shadow-md',
    'flex items-start gap-3 outline-none',
    'data-[focus-visible]:outline-2 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-primary',
  ],
  {
    variants: {
      variant: {
        success: 'border-l-4 border-l-success/80',
        error: 'border-l-4 border-l-failure/80',
        info: 'border-l-4 border-l-primary/80',
        warning: 'border-l-4 border-l-warning/80',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  },
);

const toastContentVariants = cva('flex min-w-0 flex-1 flex-col');

const toastTitleVariants = cva('text-sm font-semibold text-foreground');

const toastDescriptionVariants = cva('mt-1 text-xs text-foreground/60');

const toastCloseButtonVariants = cva(
  'flex size-6 flex-shrink-0 items-center justify-center rounded-full text-[rgba(7,20,41,0.6)] transition-colors hover:bg-neutral/10 hover:text-[rgba(7,20,41,0.8)]',
);

const toastActionButtonVariants = cva([
  'mt-3 flex h-8 w-fit items-center justify-center rounded-[6px] px-[15px]',
  'text-sm leading-[22px] font-normal',
  'border border-[rgba(7,20,41,0.2)] bg-white text-[#071429]',
  'transition-all duration-200',
  'hover:border-[rgba(7,20,41,0.3)] hover:bg-neutral/5',
  'focus:ring-2 focus:ring-[rgba(7,20,41,0.2)] focus:ring-offset-2 focus:outline-none',
  'active:scale-95',
  'shadow-sm',
]);

export interface ToastRegionProps {
  closeButton?: boolean;
}

/**
 * ToastRegion component that renders a React Aria ToastRegion
 * This provides a region for displaying toast notifications
 */
export const ToastRegion = ({ closeButton = false }: ToastRegionProps) => {
  const shouldReduceMotion = useReducedMotion();

  return (
    <AriaToastRegion
      queue={toastQueue}
      className={cn('@ToastRegion', toastRegionVariants())}
    >
      {({ toast }: { toast: QueuedToast<ToastContent> }) => (
        <AnimatePresence key={toast.key}>
          <AriaToast key={toast.key} toast={toast} className={''}>
            <motion.div
              initial={{
                opacity: 0,
                x: shouldReduceMotion ? 0 : 100,
                scale: 0.95,
              }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{
                opacity: 0,
                x: shouldReduceMotion ? 0 : 100,
                scale: 0.95,
              }}
              transition={{
                duration: 0.2,
                ease: 'easeInOut',
              }}
              className={cn(
                toastVariants({
                  variant: toast.content.variant || 'info',
                }),
              )}
            >
              <AriaToastContent className={toastContentVariants()}>
                <Text slot="title" className={toastTitleVariants()}>
                  {toast.content.title}
                </Text>
                {toast.content.description && (
                  <Text
                    slot="description"
                    className={toastDescriptionVariants()}
                  >
                    {toast.content.description}
                  </Text>
                )}
                {toast.content.action && (
                  <Button
                    onPress={() => {
                      toast.content.action?.onPress();
                      toastQueue.close(toast.key);
                    }}
                    className={toastActionButtonVariants()}
                  >
                    {toast.content.action.label}
                  </Button>
                )}
              </AriaToastContent>
              {closeButton && (
                <Button
                  slot="close"
                  className={toastCloseButtonVariants()}
                  aria-label="Close"
                >
                  <X size={14} />
                </Button>
              )}
            </motion.div>
          </AriaToast>
        </AnimatePresence>
      )}
    </AriaToastRegion>
  );
};

export const Toaster = ToastRegion;
